import React from 'react';
import { Form } from 'react-bootstrap';
import { AccountStatus } from '../types/AccountStatus.ts';

interface AccountStatusDropdownProps {
  value: AccountStatus;
  onChange: (status: AccountStatus) => void;
  isInvalid?: boolean;
  size?: 'sm' | 'lg';
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

interface LabelProps {
  label?: string;
  required?: boolean;
  helpText?: string;
}

type Props = AccountStatusDropdownProps & LabelProps;

/**
 * The display name for a stored status value.
 *
 * Module scope rather than inside the component so the details modal can use it too:
 * it rendered the raw enum, so a churned merchant read "churned_tms".
 *
 * Note AccountStatus.Active is the empty string, so an unset status and an explicitly
 * active one are the same value here and both come out "Active".
 */
export const formatStatusLabel = (status: AccountStatus): string => {
  switch (status) {
    case AccountStatus.Churned:
      return 'Churned';
    case AccountStatus.Churned_TMS:
      return 'Churned TMS';
    default:
      return 'Active';
  }
};

const AccountStatusDropdown: React.FC<Props> = ({
  value,
  onChange,
  isInvalid = false,
  size,
  disabled = false,
  className = '',
  label,
  required = false,
  helpText,
  'data-testid': testId,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    onChange(selectedValue as AccountStatus);
  };

  const statusOptions = Object.values(AccountStatus);

  return (
    <Form.Group>
      {label && (
        <Form.Label>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
          {helpText && (
            <span className="form-text ms-2">({helpText})</span>
          )}
        </Form.Label>
      )}
      <Form.Select
        value={value}
        onChange={handleChange}
        isInvalid={isInvalid}
        size={size}
        disabled={disabled}
        className={className}
        data-testid={testId}
      >
        {statusOptions.map((status) => (
          <option key={status} value={status}>
            {formatStatusLabel(status)}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
};

export default AccountStatusDropdown;